import * as E from 'fp-ts/lib/Either'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'
import { sequenceS } from 'fp-ts/lib/Apply'

import {
  AP,
  eurosInRangeV,
  Validator,
  nonEmptyArray,
  nonNullable,
  required,
  metaPath,
  metaProp,
  gte,
  maxLength
} from '@espoon-voltti/ts-libs/lib/validation'
import { EuroCents } from '@espoon-voltti/ts-libs/lib/euro'

import { maybeFormikErrors } from '@espoon-voltti/react-voltti/components/form/formik-util'

import { GeneralGrantFormData } from '@espoon-voltti/nuora-domain/grants'
import { DraftGeneralGrantFormData } from '../../../domain'

import {
  ValidGeneralGrantDraftApp,
  GeneralGrantDraftApp
} from '../../../domain'

import { eurosMax, eurosZero, eurosNegativeMin } from './constants'
import { sumOf } from '../../../utils'
import { nuoraOrgV } from './applicant'

export * from './constants'

export const getAppErrors = (app: GeneralGrantDraftApp) =>
  pipe(generalGrantDraftAppV(app), maybeFormikErrors, O.toUndefined)

export const maybeValidGeneralGrantApp = (
  app: GeneralGrantDraftApp
): O.Option<ValidGeneralGrantDraftApp> =>
  pipe(generalGrantDraftAppV(app), O.fromEither)

export const generalGrantDraftAppV: Validator<
  GeneralGrantDraftApp,
  ValidGeneralGrantDraftApp
> = (x) => {
  return pipe(
    sequenceS(AP)({
      form: formV(['form'])(x.form),
      nuoraOrg: nuoraOrgV(['applicant', 'nuoraOrg'])(x.applicant.nuoraOrg)
    }),
    E.map((valid) => ({
      id: x.id,
      modifiedAt: x.modifiedAt,
      grantPeriodId: x.grantPeriodId,
      state: x.state,
      form: valid.form,
      applicant: {
        ...x.applicant,
        nuoraOrg: valid.nuoraOrg
      }
    }))
  )
}

const formV =
  (
    path: ReadonlyArray<string>
  ): Validator<DraftGeneralGrantFormData, GeneralGrantFormData> =>
  (x) =>
    pipe(
      sequenceS(AP)({
        type: E.right(x.type),
        detailsOfUse: pipe(
          x.detailsOfUse,
          required([...path, 'detailsOfUse']),
          E.chain(maxLength([...path, 'detailsOfUse'], 500))
        ),
        goals: pipe(
          sequenceS(AP)({
            goals1: pipe(
              x.goals.goals1,
              required([...path, 'goals', 'goals1']),
              E.chain(maxLength([...path, 'goals', 'goals1'], 150))
            ),
            goals2: pipe(
              x.goals.goals2,
              maxLength([...path, 'goals', 'goals2'], 150)
            ),
            goals3: pipe(
              x.goals.goals3,
              maxLength([...path, 'goals', 'goals3'], 150)
            )
          })
        ),
        participants: pipe(
          sequenceS(AP)({
            from7to20: nonNullable<number>([
              ...path,
              'participants',
              'from7to20'
            ])(x.participants.from7to20),
            from21to28: nonNullable<number>([
              ...path,
              'participants',
              'from21to28'
            ])(x.participants.from21to28),
            [metaProp('total')]: gte(
              metaPath([...path, 'participants', 'total']),
              1
            )(sumOf([x.participants.from7to20, x.participants.from21to28]))
          })
        ),
        youngEspoo: required([...path, 'youngEspoo'])(x.youngEspoo),
        financialPlan: required([...path, 'financialPlan'])(x.financialPlan),
        previousYear: pipe(
          sequenceS(AP)({
            totalProfitInCents: pipe(
              x.previousYear.totalProfitInCents,
              nonNullable([...path, 'previousYear', 'totalProfitInCents']),
              E.chain(
                eurosInRangeV(
                  [...path, 'previousYear', 'totalProfitInCents'],
                  eurosNegativeMin,
                  eurosMax
                )
              )
            ),
            bankAccountSumInCents: pipe(
              x.previousYear.bankAccountSumInCents,
              nonNullable([...path, 'previousYear', 'bankAccountSumInCents']),
              E.chain(
                eurosInRangeV(
                  [...path, 'previousYear', 'bankAccountSumInCents'],
                  eurosNegativeMin,
                  eurosMax
                )
              )
            ),
            selfAcquiredFundsInCents: pipe(
              x.previousYear.selfAcquiredFundsInCents,
              nonNullable([
                ...path,
                'previousYear',
                'selfAcquiredFundsInCents'
              ]),
              E.chain(
                eurosInRangeV(
                  [...path, 'previousYear', 'selfAcquiredFundsInCents'],
                  eurosZero,
                  eurosMax
                )
              )
            ),
            receivedGrants: required([
              ...path,
              'previousYear',
              'receivedGrants'
            ])(x.previousYear.receivedGrants)
          })
        ),
        appliedSum: pipe(
          x.appliedSum,
          nonNullable([...path, 'appliedSum']),
          E.chain(
            eurosInRangeV(
              [...path, 'appliedSum'],
              EuroCents.fromValue(50_00),
              eurosMax
            )
          )
        ),
        attachments: pipe(
          x.attachments.filter(
            (a) =>
              a.kind === 'PublishedAttachment' ||
              a.kind === 'UploadedAttachment'
          ),
          nonEmptyArray([...path, 'attachments'])
        )
      })
    )
